import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import './styles/redesign.css';
import { setupGlobalErrorHandler } from '@/lib/errorTracking';
import { ErrorBoundaryWrapper } from '@/components/ErrorBoundaryWrapper';
import { initSentry } from '@/lib/sentry';
import { startVersionWatchdog } from '@/lib/versionWatchdog';

// No-op unless VITE_SENTRY_DSN is set and VITE_APP_ENV is staging/production.
initSentry();

// Install global unhandled-error listeners.
// setupGlobalErrorHandler is a no-op when VITE_BUG_LOGGING !== 'true' (e.g. production).
setupGlobalErrorHandler();

// Reload the page whenever Vite fails to fetch a lazy-loaded chunk after a new deploy.
// This prevents the "Failed to fetch dynamically imported module" error users see post-deploy.
window.addEventListener('vite:preloadError', () => {
    window.location.reload();
});

// The listener above only fires when an old chunk 404s. A resurrected phone
// tab whose stale chunks still resolve keeps running pre-deploy code for
// hours with no error to catch — the watchdog compares /build-info.json
// against this bundle's own sha and reloads (or offers a refresh) when they
// diverge. Seen live 2026-08-25: a 09:46 UTC deploy, still-stale tab at 15:39.
startVersionWatchdog();

const STARTUP_ERROR_HTML =
  '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;' +
  'font-family:sans-serif;padding:2rem;text-align:center;flex-direction:column;gap:1rem">' +
  '<p style="font-size:1.1rem;font-weight:600">Unable to load the application.</p>' +
  '<p style="color:#6b7280;font-size:0.9rem">Please refresh the page. If the problem persists, contact support.</p>' +
  '<button onclick="location.reload()" style="margin-top:0.5rem;padding:0.5rem 1.5rem;background:#0d9488;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:0.9rem">Refresh</button>' +
  '</div>';

const rootEl = document.getElementById("root");
if (!rootEl) {
  document.body.innerHTML = STARTUP_ERROR_HTML;
  throw new Error("Root element #root not found in DOM");
}

try {
  const root = createRoot(rootEl);
  root.render(
    <ErrorBoundaryWrapper componentName="Root">
      <App />
    </ErrorBoundaryWrapper>
  );
} catch (err) {
  console.error('[main] Fatal startup error:', err);
  rootEl.innerHTML = STARTUP_ERROR_HTML;
}
